import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type OnboardingState = {
  created_first_deal_at: string | null;
  logged_first_activity_at: string | null;
  set_first_next_step_at: string | null;
  viewed_leaderboard_at: string | null;
  enabled_notifications_at: string | null;
  dismissed_at: string | null;
};

const EMPTY: OnboardingState = {
  created_first_deal_at: null,
  logged_first_activity_at: null,
  set_first_next_step_at: null,
  viewed_leaderboard_at: null,
  enabled_notifications_at: null,
  dismissed_at: null,
};

export type OnboardingStepKey =
  | 'created_first_deal_at'
  | 'logged_first_activity_at'
  | 'set_first_next_step_at'
  | 'viewed_leaderboard_at'
  | 'enabled_notifications_at';

export function useOnboarding() {
  const { mode, userId } = useAuth();
  const [state, setState] = useState<OnboardingState>(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (mode !== 'real' || !userId) {
      setState(EMPTY);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('user_onboarding')
      .select('created_first_deal_at, logged_first_activity_at, set_first_next_step_at, viewed_leaderboard_at, enabled_notifications_at, dismissed_at')
      .eq('user_id', userId)
      .maybeSingle();
    setState(data ?? EMPTY);
    setLoading(false);
  }, [mode, userId]);

  useEffect(() => { load(); }, [load]);

  const markStep = useCallback(async (step: OnboardingStepKey) => {
    if (mode !== 'real' || !userId) return;
    if (state[step]) return; // already done
    const now = new Date().toISOString();
    setState(prev => ({ ...prev, [step]: now }));
    const row = { user_id: userId, [step]: now } as never;
    await supabase.from('user_onboarding').upsert(row, { onConflict: 'user_id' });
  }, [mode, userId, state]);

  const dismiss = useCallback(async () => {
    if (mode !== 'real' || !userId) return;
    const now = new Date().toISOString();
    setState(prev => ({ ...prev, dismissed_at: now }));
    await supabase
      .from('user_onboarding')
      .upsert({ user_id: userId, dismissed_at: now } as never, { onConflict: 'user_id' });
  }, [mode, userId]);

  const steps = [
    state.created_first_deal_at,
    state.logged_first_activity_at,
    state.set_first_next_step_at,
    state.viewed_leaderboard_at,
    state.enabled_notifications_at,
  ];
  const completedCount = steps.filter(Boolean).length;
  const allComplete = completedCount === steps.length;
  const visible = mode === 'real' && !loading && !state.dismissed_at && !allComplete;

  return { state, loading, markStep, dismiss, completedCount, totalSteps: steps.length, allComplete, visible, refresh: load };
}
